import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { GraduationCap, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, UtensilsCrossed, Waves } from 'lucide-react';

const SchoolsEducationalFacilities = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "Do you clean during term time or only school holidays?",
      a: "We schedule this work during school holiday periods, when the site is empty. This allows for a genuine, thorough deep clean without disrupting students or staff."
    },
    {
      q: "What's included in a school canteen clean?",
      a: "Canteen equipment cleaning and canopy exhaust cleaning, along with the surrounding kitchen area, as part of a full canteen deep clean."
    },
    {
      q: "Do you clean sports courts as well?",
      a: "Yes, both indoor and outdoor basketball courts, along with high-pressure cleaning of outdoor concrete grounds."
    },
    {
      q: "Do you clean carpets and windows throughout the school?",
      a: "Yes, commercial carpet cleaning and window cleaning are included as part of a full school facility clean."
    }
  ];

  // Service Schema for SEO - Perth metro only
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "School & Educational Facility Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep"
    ],
    "description": "Deep cleaning for schools and educational facilities across Perth, scheduled around school holidays. Canteen cleaning, sports courts, carpets, windows and outdoor grounds."
  };

  const relatedServices = [
    {
      title: 'Commercial Kitchen Cleaning',
      description: 'Canteen deep cleaning as part of a full kitchen service.',
      link: '/services/commercial-kitchen-cleaning-perth',
      icon: UtensilsCrossed
    },
    {
      title: 'Commercial High-Pressure Cleaning',
      description: 'Outdoor concrete grounds and sports court cleaning.',
      link: '/services/commercial-high-pressure-cleaning-perth',
      icon: Waves
    }
  ];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/industries/schools-educational-facilities-perth" />
        <title>School & Educational Facility Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Deep cleaning for schools across Perth, scheduled around school holidays. Canteens, sports courts, carpets, windows and outdoor grounds. No disruption to the school day." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="schools-educational-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1740635341299-3b8e3490f546?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjbGFzc3Jvb20lMjBkZXNrcyUyMGNoYWlycyUyMGJvb2tzJTIwc3VwcGxpZXMlMjBtZXNzeXxlbnwwfHx8fDE3ODk2MTI5NDR8MA&ixlib=rb-4.1.0&q=85"
              alt="Classroom with desks, chairs and school supplies"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <GraduationCap size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Educational Facilities</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                School & Educational Facility Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Deep cleaning scheduled around school holidays, so the work never disrupts a single school day
              </p>
              <p className="text-gray-400 mb-8">
                Schools need a different kind of clean to a typical commercial site, canteens, sports courts, and shared facilities that see heavy daily use and need a genuine deep clean, not just routine upkeep. We schedule this work for schools across Perth around holiday periods specifically, so the site is empty and the whole job can be done properly without working around students or staff.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                Canteen deep cleaning, including kitchen equipment and canopy exhaust cleaning, is a core part of this work. We also clean indoor and outdoor basketball courts, high-pressure clean outdoor concrete grounds, and handle window cleaning and commercial carpet cleaning throughout the facility. Scope is built around what each school actually needs, not a fixed package.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Canteen deep cleaning',
                  'Kitchen equipment cleaning',
                  'Canopy exhaust cleaning',
                  'Indoor basketball courts',
                  'Outdoor sports courts',
                  'High-pressure ground cleaning',
                  'Window cleaning',
                  'Commercial carpet cleaning'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Scheduled Around School Holidays */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scheduling</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Scheduled Around School Holidays</h2>
              <p className="text-gray-600">
                This work is scheduled during school holiday periods, when the site is empty. That means a genuine deep clean, canteens, courts, carpets and windows, can be carried out properly and thoroughly, without needing to work around students, staff or the school day.
              </p>
            </div>
          </div>
        </section>

        {/* Real Experience */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Experience</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Real Experience with School Facilities</h2>
              <p className="text-gray-300">
                Cleaning schools and educational facilities is genuine, hands-on experience for our team, built up over years of working in canteens, sports facilities and shared school spaces. Combined with the same standard we bring to commercial kitchens and industrial sites, that's a level of care schools can rely on for a proper holiday deep clean.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              <p className="text-gray-600">
                We provide school and educational facility cleaning across the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
              </p>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your School Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for school holiday deep cleaning across Perth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SchoolsEducationalFacilities;
